import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ClarityTags, useClarityTags, DEFAULT_PAGE_SIZE } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { usePreviewAndPanel } from './hooks/use-preiview-and-panel';
import { AssetDetailsPageLayout } from './components/details-page-layout';
import { useDetailsPageSetup } from './hooks/use-details-page-setup';
import { useAssetDetailsPageActions } from './hooks/use-asset-details-page-actions';

export const AssetDetailsPage = ({
  assetId, dataService, logger, permissions, pageSize, navigation, open, i18n, assets, totalAssetCount, folderAssetType, isReadonly, notificationApi, loadMoreData, renameAsset, getGranularityEnum, perfMetricDateRange, onClose,
}) => {
  useClarityTags([ClarityTags.LaunchedAssetLibrary, ClarityTags.LaunchedImageDetails]);

  const { getImageById, loadPerfTimeSeriesData } = dataService || {};
  const { AssetPreview, AssetDetailsPanel } = usePreviewAndPanel({ folderAssetType });
  const {
    navigateToFallbackPage,
    navigateToOtherItem,
    reloadLandingPageRef,
  } = useDetailsPageSetup({
    currentItemId: assetId,
    folderAssetType,
    logger,
    navigation,
    assets,
    totalAssetCount,
    loadMoreData,
    renameAsset,
    onClose,
  });

  const {
    handleDeleteAsset,
    handleRenameAsset,
    gotoAdsPageWithAssetIdFilter,
    copyUrl,
  } = useAssetDetailsPageActions({
    dataService,
    navigation,
    assetId,
    folderAssetType,
    reloadLandingPageRef,
    renameAsset,
  });

  useEffect(() => {
    logger.logTrace('Asset Details: page loaded', { assetId });
  }, [assetId, logger]);

  const renderAssetPreview = useCallback(({
    itemData,
    shouldHideContent,
    currentIndex,
    gotoNext,
    gotoPrevious,
  }) => (
    <AssetPreview
      itemInfo={itemData}
      currentIndex={currentIndex}
      totalCount={totalAssetCount || 0}
      shouldHideContent={shouldHideContent}
      onNext={gotoNext}
      onPrevious={gotoPrevious}
      i18n={i18n}
    />
  ), [i18n, totalAssetCount, AssetPreview]);

  const renderAssetDetailsView = useCallback(({ itemData, dismiss, footerKeyDownHandler }) => (
    <AssetDetailsPanel
      isOpen={open}
      assetInfo={itemData}
      i18n={i18n}
      getImageById={getImageById}
      isReadonly={isReadonly}
      footerKeyDownHandler={footerKeyDownHandler}
      logger={logger}
      navigation={navigation}
      handleDeleteAsset={handleDeleteAsset}
      handleRenameAsset={handleRenameAsset}
      onDismiss={dismiss}
      notificationApi={notificationApi}
      copyUrl={copyUrl}
      gotoAdsPageWithAssetIdFilter={gotoAdsPageWithAssetIdFilter}
      loadPerfTimeSeriesData={loadPerfTimeSeriesData}
      getGranularityEnum={getGranularityEnum}
      perfMetricDateRange={perfMetricDateRange}
      dataService={dataService}
      permissions={permissions}
    />
  ), [AssetDetailsPanel, open, i18n, getImageById, isReadonly, logger, navigation, handleDeleteAsset, handleRenameAsset, notificationApi, copyUrl, gotoAdsPageWithAssetIdFilter, loadPerfTimeSeriesData, getGranularityEnum, perfMetricDateRange, permissions]);

  return (
    open && (
      <AssetDetailsPageLayout
        navigation={navigation}
        navigateToOtherItem={navigateToOtherItem}
        navigateToFallbackPage={navigateToFallbackPage}
        itemId={assetId}
        loadedItems={assets}
        loadMoreItemsAsync={loadMoreData}
        renderItemPreview={renderAssetPreview}
        renderItemDetails={renderAssetDetailsView}
        permissions={permissions}
        pageSize={pageSize}
        reloadLandingPageRef={reloadLandingPageRef}
        onClose={onClose}
      />
    )
  );
};

AssetDetailsPage.propTypes = {
  dataService: PropTypes.shape({}).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoEditImage: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    redirectToLandingPage: PropTypes.func.isRequired,
    redirectToEditImage: PropTypes.func.isRequired,
    redirectToItemDetail: PropTypes.func.isRequired,
    gotoAdsPageWithFilter: PropTypes.func.isRequired,
  }).isRequired,
  assetId: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      SmartMediaGenerationPrototype: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  pageSize: PropTypes.number,
  open: PropTypes.bool.isRequired,
};

AssetDetailsPage.defaultProps = {
  pageSize: DEFAULT_PAGE_SIZE,
};
